import { type ReactNode, useState, useEffect, useCallback, useSyncExternalStore } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FolderKanban,
  ClipboardCheck,
  FileWarning,
  FileText,
  Files,
  Send,
  User,
  LogOut,
  Wrench,
  Sun,
  Moon,
  HelpCircle,
  Menu,
  X,
  WifiOff,
} from 'lucide-react'
import { useAuth } from '../../auth'
import { GlobalSearch } from './GlobalSearch'
import { HelpMenu } from './HelpMenu'
import { searchApi } from '../../services/searchApi'

interface LayoutProps {
  children: ReactNode
}

function subscribeOnline(callback: () => void) {
  window.addEventListener('online', callback)
  window.addEventListener('offline', callback)
  return () => {
    window.removeEventListener('online', callback)
    window.removeEventListener('offline', callback)
  }
}

function getOnlineSnapshot() {
  return navigator.onLine
}

function useOnlineStatus() {
  return useSyncExternalStore(subscribeOnline, getOnlineSnapshot)
}

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('theme') as 'dark' | 'light') || 'dark'
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark')
  return { theme, toggle }
}

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, badgeKey: null },
  { to: '/my-assignments', label: 'My Work', icon: User, badgeKey: null },
  { to: '/projects', label: 'Projects', icon: FolderKanban, badgeKey: null },
  { to: '/checklists', label: 'Checklists', icon: ClipboardCheck, badgeKey: null },
  { to: '/issues', label: 'Issues Log', icon: FileWarning, badgeKey: 'issues' as const },
  { to: '/cx-report', label: 'Cx Report', icon: FileText, badgeKey: null },
  { to: '/submittals', label: 'Submittals', icon: Send, badgeKey: null },
  { to: '/documents', label: 'Documents', icon: Files, badgeKey: null },
]

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const { theme, toggle: toggleTheme } = useTheme()
  const isOnline = useOnlineStatus()
  const [overdueCounts, setOverdueCounts] = useState<{ issues: number }>({ issues: 0 })
  const [showHelp, setShowHelp] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false) }, [location.pathname])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if ((e.target as HTMLElement)?.isContentEditable) return
      e.preventDefault()
      setShowHelp(prev => !prev)
    }
    if (e.key === 'Escape' && showHelp) {
      setShowHelp(false)
    }
  }, [showHelp])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    searchApi.getOverdue().then(data => {
      setOverdueCounts({
        issues: data.counts?.issues ?? 0,
      })
    }).catch(() => {})
  }, [])

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  return (
    <div className="app-container">
      {!isOnline && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: '#b45309',
          color: '#fff',
          textAlign: 'center',
          padding: '6px 12px',
          fontSize: '13px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <WifiOff size={14} />
          You are offline. Showing cached data.
        </div>
      )}
      {/* Mobile header bar */}
      <div className="mobile-header">
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <Menu size={24} />
        </button>
        <div className="mobile-header-title">
          <Wrench size={20} color="var(--accent-blue)" />
          <span>CxRunner</span>
        </div>
      </div>

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Wrench size={36} color="var(--accent-blue)" />
          </div>
          <div>
            <div className="sidebar-title">CxRunner</div>
            <div className="sidebar-subtitle">Commissioning Mgmt</div>
          </div>
          <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav" style={{ position: 'relative' }}>
          <GlobalSearch />
          <div className="nav-section">
            <div className="nav-section-title">Navigation</div>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                <item.icon size={20} className="nav-link-icon" />
                <span>{item.label}</span>
                {item.badgeKey && overdueCounts[item.badgeKey] > 0 && (
                  <span className="nav-link-badge">{overdueCounts[item.badgeKey]}</span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{initials}</div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-email">{user?.email || ''}</div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="logout-btn" onClick={() => setShowHelp(true)} style={{ width: 'auto', flex: '0 0 auto', padding: '6px 10px' }} title="Help & Guide (?)">
              <HelpCircle size={16} />
            </button>
            <button className="logout-btn" onClick={toggleTheme} style={{ width: 'auto', flex: '0 0 auto', padding: '6px 10px' }} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button className="logout-btn" onClick={logout}>
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content">
        {children}
      </main>

      <HelpMenu isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  )
}
